import {
  AppBar,
  Badge,
  IconButton,
  InputBase,
  Slide,
  Toolbar,
  Tooltip,
  useScrollTrigger
} from '@material-ui/core';
import {
  createStyles,
  fade,
  makeStyles,
  Theme
} from '@material-ui/core/styles';
import {
  ChatBubbleOutlineRounded,
  MenuRounded,
  SearchRounded
} from '@material-ui/icons';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RRLink, useHistory } from 'react-router-dom';
import { showBadge } from '../../constants/menuTabs';
import BackspaceLogo from '../BackspaceLogo';
import BellNotification from '../BellNotification';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1
    },
    appBar: {
      backgroundColor:
        theme.palette.navbar && fade(theme.palette.navbar.main, 0.7),
      color: theme.palette.text.primary,
      backdropFilter: 'blur(8px)'
    },
    toolbar: {
      display: 'flex'
    },
    menuButton: {
      margin: theme.spacing(0, 1, 0, -2),
      [theme.breakpoints.down('xs')]: {
        margin: theme.spacing(0, -1, 0, 0),
        order: 1
      }
    },
    chatButton: {
      margin: theme.spacing(0, -2, 0, 0),
      [theme.breakpoints.down('xs')]: {
        margin: theme.spacing(0, -0.5, 0, 0)
      }
    },
    title: {
      flexGrow: 1,
      [theme.breakpoints.down('xs')]: {
        marginLeft: theme.spacing(0.5)
      },
      display: 'flex'
    },
    flexGrow: {
      flexGrow: 1
    },
    search: {
      display: 'none',
      position: 'relative',
      borderRadius: theme.shape.borderRadius,
      backgroundColor: fade(theme.palette.common.black, 0.04),
      '&:hover': {
        backgroundColor: fade(theme.palette.common.black, 0.08)
      },
      marginLeft: 0,
      marginRight: theme.spacing(0.5),
      width: '100%',
      [theme.breakpoints.up('sm')]: {
        marginLeft: theme.spacing(1),
        width: 'auto'
      }
    },
    searchIcon: {
      padding: theme.spacing(0, 1),
      height: '100%',
      position: 'absolute',
      pointerEvents: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    searchButton: {
      display: 'none'
    },
    inputRoot: {
      color: theme.palette.text.secondary
    },
    inputInput: {
      padding: theme.spacing(1, 1, 1, 0),
      // vertical padding + font size from searchIcon
      paddingLeft: `calc(1em + ${theme.spacing(3)}px)`,
      transition: theme.transitions.create('width'),
      [theme.breakpoints.up('sm')]: {
        width: '16ch',
        '&:focus': {
          width: '24ch'
        }
      }
    }
  })
);

interface NavbarProps {
  handleDrawer: (
    open: boolean
  ) => (event: React.KeyboardEvent | React.MouseEvent) => void;
}

export default function Navbar({ handleDrawer }: NavbarProps) {
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation();

  const trigger = useScrollTrigger({
    target: window ? window : undefined
  });

  const [search, setSearch] = useState<string>();

  function handleSearch(event: any) {
    if (event.target.value) {
      const search = event.target.value
        .replaceAll('+', '%2B')
        .replaceAll(' ', '+');
      history.push(`/search?q=${search}`);
      setSearch('');
    }
  }

  return (
    <div className={classes.root}>
      <Slide appear={false} direction="down" in={!trigger}>
        <AppBar className={classes.appBar}>
          <Toolbar className={classes.toolbar}>
            <Tooltip title={t('navbar.menu') as string}>
              <IconButton
                edge="start"
                className={classes.menuButton}
                color="inherit"
                aria-label={t('navbar.menuAriaLabel') as string}
                onClick={handleDrawer(true)}
              >
                <Badge color="secondary" variant="dot" invisible={!showBadge}>
                  <MenuRounded />
                </Badge>
              </IconButton>
            </Tooltip>
            <div className={classes.title}>
              {BackspaceLogo('document')}
              <div className={classes.flexGrow} />
            </div>
            <div className={classes.search}>
              <div className={classes.searchIcon}>
                <SearchRounded />
              </div>
              <InputBase
                placeholder={t('navbar.searchPlaceholder') as string}
                classes={{
                  root: classes.inputRoot,
                  input: classes.inputInput
                }}
                fullWidth
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                }}
                onKeyPress={(event) => {
                  if (event.key === 'Enter') {
                    handleSearch(event);
                  }
                }}
                inputProps={{ 'aria-label': t('navbar.searchAriaLabel') as string }}
              />
            </div>
            <Tooltip title={t('navbar.search') as string}>
              <IconButton
                className={classes.searchButton}
                component={RRLink}
                to={'/search'}
                color="inherit"
                aria-label={t('navbar.searchAriaLabel') as string}
              >
                <SearchRounded />
              </IconButton>
            </Tooltip>
            <BellNotification />
            <Tooltip title={t('navbar.contactSupport') as string}>
              <IconButton
                edge="end"
                className={classes.chatButton}
                color="inherit"
                aria-label={t('navbar.contactSupportAriaLabel') as string}
                onClick={() => {
                  window.open(
                    `${process.env.REACT_APP_ENC_PLATFORM_SUPPORT}`,
                    '_blank'
                  );
                }}
              >
                <ChatBubbleOutlineRounded />
              </IconButton>
            </Tooltip>
          </Toolbar>
        </AppBar>
      </Slide>
    </div>
  );
}
